import React from "react";
import classNames from "classnames";
import Avatar from "../../theme/untitled/Avatar/Avatar";
import Icon from "../../theme/untitled/icons/Icon";
import Badge from "../../theme/untitled/Badge/Badge";
import IconButton from "../../theme/untitled/IconButton/IconButton";
import RowActionsMenu from "./RowActionsMenu";
import { formatRelativeTime } from "./relativeTime";
import { ROLE_LABELS, ROLE_OWNER } from "./constants";
import styles from "./MembersPage.module.scss";

const NameCell = ({ row }) => (
  <div className={styles.nameCell}>
    {row.status === "pending" ? (
      <Avatar className={styles.avatar} icon={<Icon name="AtSign" size={18} />} />
    ) : (
      <Avatar className={styles.avatar} initials={row.initials} />
    )}
    <div className={styles.nameBlock}>
      <div className={styles.nameLine}>
        {row.name}
        {row.isCurrentUser && <span className={styles.youPill}>You</span>}
      </div>
      <span className={styles.metaLine}>
        {row.status === "pending"
          ? row.createdAt
            ? `Invited ${formatRelativeTime(row.createdAt)}`
            : "Invited"
          : row.email}
      </span>
    </div>
  </div>
);

const LastActive = ({ row }) =>
  row.status === "active" && row.lastLogin ? (
    formatRelativeTime(row.lastLogin)
  ) : (
    <span className={styles.dash}>&mdash;</span>
  );

const MembersTable = ({
  rows,
  ownerCount,
  busyRowId,
  rowError,
  onDismissRowError,
  onChangeRole,
  onOpenRemove,
  onResendInvite,
  onRevokeInvite,
}) => (
  <table className={styles.table}>
    <colgroup>
      <col className={styles.colName} />
      <col className={styles.colRole} />
      <col className={styles.colStatus} />
      <col className={styles.colLast} />
      <col className={styles.colActions} />
    </colgroup>
    <thead>
      <tr>
        <th scope="col">Name</th>
        <th scope="col">Role</th>
        <th scope="col">Status</th>
        <th scope="col" style={{ textAlign: "right" }}>
          Last active
        </th>
        <th scope="col" aria-hidden="true" />
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => {
        const isSoleOwner = row.role === ROLE_OWNER && ownerCount === 1;
        const isBusy = busyRowId === row.uuid;
        return (
          <React.Fragment key={row.uuid}>
            <tr className={classNames(styles.row, isBusy && styles.rowBusy)}>
              <td>
                <NameCell row={row} />
              </td>
              <td>
                {isBusy ? (
                  <span className={styles.roleCellBusy}>
                    <span className={styles.spinnerSm} aria-hidden="true" />
                    Saving…
                  </span>
                ) : (
                  ROLE_LABELS[row.role]
                )}
              </td>
              <td>
                {row.status === "pending" ? (
                  <Badge color="warning" size="sm" dot>
                    Pending
                  </Badge>
                ) : (
                  "Active"
                )}
              </td>
              <td className={styles.lastActiveCell} style={{ textAlign: "right" }}>
                <LastActive row={row} />
              </td>
              <td style={{ textAlign: "right" }}>
                {!isBusy && (
                  <RowActionsMenu
                    row={row}
                    isSoleOwner={isSoleOwner}
                    className={styles.rowKebab}
                    onChangeRole={onChangeRole}
                    onOpenRemove={onOpenRemove}
                    onResendInvite={onResendInvite}
                    onRevokeInvite={onRevokeInvite}
                  />
                )}
              </td>
            </tr>
            {rowError?.rowId === row.uuid && (
              <tr>
                <td colSpan={5} style={{ padding: 0 }}>
                  <div className={styles.rowError}>
                    <Icon name="AlertCircle" size={20} className={styles.rowErrorIcon} />
                    <span>{rowError.message}</span>
                    <span className={styles.rowErrorActions}>
                      <button type="button" onClick={rowError.retry}>
                        Try again
                      </button>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={<Icon name="XClose" />}
                        label="Dismiss"
                        onClick={onDismissRowError}
                      />
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  </table>
);

export default MembersTable;
